/**
 * GDriveSettings — slide-in panel for Produktion Google Drive configuration.
 *
 * Reads/writes config/gdriveSettings in Firestore:
 *   sourceFolderId    — Drive folder to search for PNG files (aktuell)
 *   destFolderId      — Drive folder to move files into (Druck_Auto)
 *   localCopy         — bool: also download to local path
 *   localPath         — local directory for downloads
 *   autoLocalDropPath — local Google-Drive-for-Desktop mount of the source
 *                       folder; the scraper writes auto-rendered print files
 *                       here after the customer approves the draft
 *   autoUseApiUpload  — bool: use the Drive API instead of the local mount.
 *                       Only works in a Shared Drive — a service account has
 *                       no Drive storage quota and cannot create files in a
 *                       personal My Drive (403 storageQuotaExceeded).
 */
import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

const DEFAULTS = {
  sourceFolderId:    "1ZptFWJMIfM7qRipLqAwTqZCn4QzanpnV",
  destFolderId:      "14xBRflVYjy66CjbCeKq62XDXjKfLQ5Uz",
  localCopy:         false,
  localPath:         "C:\\Bilddateien_Printsonalities",
  autoLocalDropPath: "",
  autoUseApiUpload:  false,
};

export default function GDriveSettings({ onClose }) {
  const [cfg,     setCfg]     = useState(DEFAULTS);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDoc(doc(db, "config", "gdriveSettings")).then((snap) => {
      if (snap.exists()) setCfg({ ...DEFAULTS, ...snap.data() });
    }).finally(() => setLoading(false));
  }, []);

  const set = (field, value) => {
    setCfg((c) => ({ ...c, [field]: value }));
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "gdriveSettings"), cfg);
      setSaved(true);
    } finally {
      setSaving(false);
    }
  };

  const overlay = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.25)",
    zIndex: 200,
    display: "flex", justifyContent: "flex-end",
  };

  const panel = {
    width: 420, height: "100%",
    background: "#fff",
    boxShadow: "-4px 0 20px rgba(0,0,0,0.12)",
    padding: "2em 1.8em",
    overflowY: "auto",
    display: "flex", flexDirection: "column", gap: "1.4em",
  };

  const label = {
    display: "block",
    fontWeight: "600",
    fontSize: "0.78rem",
    color: "#4a5568",
    marginBottom: "0.3em",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  };

  const input = {
    width: "100%",
    padding: "0.5em 0.75em",
    borderRadius: "6px",
    border: "1px solid #cbd5e0",
    fontSize: "0.85rem",
    fontFamily: "monospace",
    boxSizing: "border-box",
  };

  if (loading) return null;

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={panel}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: "700", fontSize: "1rem", color: "#1a202c" }}>
            GDrive Einstellungen
          </span>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", fontSize: "1.3rem",
                     cursor: "pointer", color: "#718096", lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div>
          <span style={label}>Quellordner-ID</span>
          <input
            style={input}
            value={cfg.sourceFolderId}
            onChange={(e) => set("sourceFolderId", e.target.value)}
            placeholder="Google Drive Ordner-ID"
          />
          <span style={{ fontSize: "0.72rem", color: "#a0aec0" }}>
            ID aus der Drive-URL (letzter Teil nach /folders/)
          </span>
        </div>

        <div>
          <span style={label}>Zielordner-ID</span>
          <input
            style={input}
            value={cfg.destFolderId}
            onChange={(e) => set("destFolderId", e.target.value)}
            placeholder="Google Drive Ordner-ID"
          />
        </div>

        <div style={{ fontSize: "0.78rem", color: "#a0aec0",
                     background: "#f7fafc", borderRadius: "6px",
                     padding: "0.5em 0.75em" }}>
          Dateien werden immer verschoben (Quelldatei wird aus dem Quellordner
          entfernt). Google Drive erlaubt keine Datei in mehreren Ordnern gleichzeitig.
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: "0.75em",
                        cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={cfg.localCopy}
            onChange={(e) => set("localCopy", e.target.checked)}
            style={{ width: 16, height: 16, cursor: "pointer" }}
          />
          <span style={{ fontSize: "0.875rem", color: "#2d3748" }}>
            Lokale Kopie erstellen (auf diesem PC)
          </span>
        </label>

        {cfg.localCopy && (
          <div>
            <span style={label}>Lokaler Pfad</span>
            <input
              style={input}
              value={cfg.localPath}
              onChange={(e) => set("localPath", e.target.value)}
              placeholder="C:\Bilddateien_Printsonalities"
            />
            <span style={{ fontSize: "0.72rem", color: "#a0aec0" }}>
              Dateien werden in Unterordner nach Datum abgelegt (z.B. 2026-04-11)
            </span>
          </div>
        )}

        <div style={{ height: 1, background: "#e2e8f0" }} />

        <div>
          <span style={label}>Ablageordner für Auto-Entwürfe</span>
          <input
            style={input}
            value={cfg.autoLocalDropPath}
            onChange={(e) => set("autoLocalDropPath", e.target.value)}
            placeholder="G:\\Meine Ablage\\aktuell"
          />
          <span style={{ fontSize: "0.72rem", color: "#a0aec0" }}>
            Lokaler Pfad des Quellordners in Google Drive for Desktop. Der
            Scraper legt die Druckdatei hier ab, sobald der Kunde den Entwurf
            freigegeben hat; Drive lädt sie dann hoch. Muss auf dem PC
            erreichbar sein, auf dem der Scraper läuft.
          </span>
        </div>

        <label style={{ display: "flex", alignItems: "flex-start",
                        gap: "0.75em", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={cfg.autoUseApiUpload}
            onChange={(e) => set("autoUseApiUpload", e.target.checked)}
            style={{ width: 16, height: 16, cursor: "pointer",
                     marginTop: "0.15em" }}
          />
          <span style={{ fontSize: "0.875rem", color: "#2d3748" }}>
            Stattdessen über die Drive-API hochladen
            <span style={{ display: "block", fontSize: "0.72rem",
                           color: "#a0aec0", marginTop: "0.15em" }}>
              Nur aktivieren, wenn der Ordner in einem Shared Drive liegt. In
              einem privaten Drive lehnt Google den Upload ab, weil der
              Service-Account kein Speicherkontingent hat.
            </span>
          </span>
        </label>

        <div style={{ display: "flex", gap: "0.75em", marginTop: "0.5em" }}>
          <button
            onClick={save}
            disabled={saving}
            style={{
              flex: 1, padding: "0.6em", borderRadius: "8px",
              background: saved ? "#d1fae5" : "#4a2c2a",
              color: saved ? "#065f46" : "#fff",
              border: "none", cursor: saving ? "not-allowed" : "pointer",
              fontWeight: "600", fontSize: "0.9rem",
              transition: "background 0.2s",
            }}
          >
            {saving ? "Speichern…" : saved ? "Gespeichert ✓" : "Speichern"}
          </button>
          <button
            onClick={onClose}
            style={{
              padding: "0.6em 1.2em", borderRadius: "8px",
              background: "transparent",
              border: "1px solid #e2e8f0",
              color: "#718096", cursor: "pointer",
              fontSize: "0.9rem",
            }}
          >
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}
